import React, { useState, useEffect, useCallback } from "react";
import { useAppBridge } from "@shopify/app-bridge-react";
import { useParams, useLocation } from "react-router-dom";
import { SubscriptionPlan } from "./SubscriptionPlan";
import axios from "axios";
import Config from "../../utils/Config";
import { redirectIframe } from "../../components/ExitIFrame/ExitIFrame";
import { reportNetworkError } from "../../utils/networkErrorReporting";
import { Card, Button, BlockStack, Text, Page} from "@shopify/polaris";

const performInstallOp = async (authQueryString, app, host) => {
    const installUrl = `${Config.apiGateway.URL}/authentication/install/${Config.appName}${authQueryString}`;
    const installStatus = await axios.get(installUrl);

    if (installStatus.status === 200) {
        if (installStatus.data.Plan === SubscriptionPlan.LegacyStarter) {
            // Refresh app with new plan.
            //redirectLocal("/", app);
            redirectIframe(`https://${host}`, app);
        } else {
            // Redirect to charge activation page.
            redirectIframe(installStatus.data.RedirectUrl, app);
        }
    }
};

export function UpdatePermissionsRoute({ host, authQueryString }) {
    const app = useAppBridge();

    useEffect(() => {
        performInstallOp(`?updatePermissions=true&${authQueryString}`, app, host);
    }, [app, authQueryString, host]);

    app.loading(true);

    return <></>;
}

export function ChangeSubscriptionRoute({ host, authQueryString }) {
    const app = useAppBridge();
    const params = useParams();
    const location = useLocation();
    const [subscriptionStatus, setSubscriptionStatus] = useState("unset");
    const [errorMessage, setErrorMessage] = useState("");

    const performSubscriptionOp = useCallback(async (targetPlan, discountCode) => {
        const upgradeDowngradeUrl = `${Config.apiGateway.URL}/subscription/${Config.appName}/change`;
        
        try {
            const response = await axios.post(upgradeDowngradeUrl, {
                TargetPlan: targetPlan,
                DiscountCode: discountCode || undefined,
            }, {
                headers: {
                    Authorization: authQueryString
                }
            });

            const data = response.data || {};
            const success = typeof data.Success !== 'undefined' ? data.Success : data.success;
            const redirectUrl = typeof data.RedirectUrl !== 'undefined' ? data.RedirectUrl : data.redirectUrl;
            const message = typeof data.Message !== 'undefined' ? data.Message : data.message;

            if (success) {
                try {
                    if (
                        targetPlan !== SubscriptionPlan.Starter &&
                        targetPlan !== SubscriptionPlan.LegacyStarter
                    ) {
                        localStorage.setItem("discoDuringUpgrade", "true");
                    } else {
                        localStorage.removeItem("discoDuringUpgrade");
                    }
                } catch (e) {
                    console.error("Failed to set upgrade flag", e);
                }

                if (redirectUrl) {
                    // Redirect to charge activation page.
                    redirectIframe(redirectUrl, app);
                } else {
                    // Refresh app with new plan.
                    redirectIframe(`https://${host}`, app);
                }
            } else {
                setSubscriptionStatus("failed");
                setErrorMessage(message || "Subscription change failed.");
            }
        } catch (error) {
            reportNetworkError(error, {
                url: upgradeDowngradeUrl,
                method: "POST",
                source: "axios",
                operationName: "subscription.change",
            });
            setSubscriptionStatus("failed");
            setErrorMessage(error.message || "An error occurred during subscription change.");
        }
    }, [host, app, authQueryString]);

    useEffect(() => {
        if (params.targetPlan) {
            const targetPlanValue = parseInt(params.targetPlan, 10);
            if (Object.values(SubscriptionPlan).includes(targetPlanValue) && 
                targetPlanValue !== SubscriptionPlan.Inactive) {
                const searchParams = new URLSearchParams(location.search);
                const discountCode = searchParams.get('discount');
                performSubscriptionOp(targetPlanValue, discountCode);
            } else {
                setSubscriptionStatus("failed");
                setErrorMessage(`Invalid or default target plan: ${params.targetPlan}`);
            }
        } else {
            setSubscriptionStatus("failed");
            setErrorMessage("No target plan specified");
        }
    }, [params.targetPlan, location.search, host, authQueryString, app, performSubscriptionOp]);

    if (subscriptionStatus === "unset") {
        app.loading(true);
        return <></>;
    }

    if (subscriptionStatus === "failed") {
        return (
            <Page>
                <Card background="bg-surface-critical">
                    <BlockStack gap={400}>
                        <Text variant="headingMd">Failed changing subscription</Text>
                        <Text variant="bodyMd">{errorMessage}</Text>
                        <Button url="/subscription" variant="primary">
                            Select another subscription plan
                        </Button>
                    </BlockStack>
                </Card>
            </Page>
        );
    }

    app.loading(true);
    return <></>;
}

// Outcome rendering for the EU Right of Withdrawal in-app flow. The real two-step
// confirmation, EU/EEA gating and statutory copy landed with #20 (see
// WithdrawalDisclosure / WithdrawalSection). The endpoint POST is fired from
// WithdrawRoute below.
//
// Note: even when the backend reports `executed`, this is NOT customer-facing
// completion — the refund is processed manually and only later closure is true
// completion. So the success state reads "request received", never "withdrawn".
const WITHDRAW_OUTCOME = {
    executed: {
        heading: "Withdrawal request received",
        background: "bg-surface-success",
        body: "Your subscription has been cancelled and billing has stopped. You'll receive an email confirming once the refund has been processed.",
    },
    outOfWindow: {
        heading: "Withdrawal window has passed",
        background: "bg-surface-warning",
        body: "The 14-day withdrawal window for your subscription has passed, so an automatic refund no longer applies.",
    },
    review: {
        heading: "Withdrawal request received",
        background: "bg-surface-warning",
        body: "Your withdrawal request has been received and will be processed shortly. You'll receive a confirmation email.",
    },
    failed: {
        heading: "Withdrawal failed",
        background: "bg-surface-critical",
        body: "Withdrawal failed.",
    },
};

export function WithdrawRoute({ authQueryString }) {
    const app = useAppBridge();
    const [result, setResult] = useState(null);

    const performWithdrawal = useCallback(async () => {
        const withdrawUrl = `${Config.apiGateway.URL}/subscription/${Config.appName}/withdraw`;

        try {
            const response = await axios.post(withdrawUrl, {}, {
                headers: { Authorization: authQueryString },
            });

            const data = response.data || {};
            const success = typeof data.Success !== "undefined" ? data.Success : data.success;
            const executed = typeof data.Executed !== "undefined" ? data.Executed : data.executed;
            const outOfWindow = typeof data.OutOfWindow !== "undefined" ? data.OutOfWindow : data.outOfWindow;
            const responseMessage = typeof data.Message !== "undefined" ? data.Message : data.message;

            if (!success) {
                setResult({ outcome: "failed", message: responseMessage });
                return;
            }

            const outcome = executed ? "executed" : outOfWindow ? "outOfWindow" : "review";
            setResult({ outcome, message: responseMessage });
        } catch (error) {
            reportNetworkError(error, {
                url: withdrawUrl,
                method: "POST",
                source: "axios",
                operationName: "subscription.withdraw",
            });
            setResult({ outcome: "failed", message: error.message || "An error occurred during withdrawal." });
        }
    }, [authQueryString]);

    useEffect(() => {
        performWithdrawal();
    }, [performWithdrawal]);

    if (!result) {
        app.loading(true);
        return <></>;
    }

    const outcome = WITHDRAW_OUTCOME[result.outcome] || WITHDRAW_OUTCOME.review;

    return (
        <Page>
            <Card background={outcome.background}>
                <BlockStack gap={400}>
                    <Text variant="headingMd">{outcome.heading}</Text>
                    <Text variant="bodyMd">{result.message || outcome.body}</Text>
                    <Button url="/subscription" variant="primary">
                        Back to subscription
                    </Button>
                </BlockStack>
            </Card>
        </Page>
    );
}
